import { Spinner } from '@creditornot/cb-progress';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { getInboundMerchant } from 'apis/merchant-onboarding-service/api';

import SsmoPageWrapper from './SsmoPageWrapper';
import Wizard from './Wizard/Wizard';
import { SelfServeStepsIdentifiers } from './constants';
import ProductListForm from './forms/ProductListForm';
import BusinessInformationForm from './stepsContent/BusinessInformationForm';
import CommissionsStep from './stepsContent/CommissionsStep';
import MerchantDeviceSelectionForm from './stepsContent/DeviceSelectionStep';
import MenuPhotoshopOptionStep from './stepsContent/MenuPhotoshootOptionStep';
import ReviewInfo from './stepsContent/ReviewInfo';
import VenueOpeningHoursForm from './stepsContent/VenueOpeningHoursForm';
import { SelfServeStepsType, SelfServeWizardStep } from './types';
import { getCountryConfigs, getSsmoCountryConfigs } from '../SelfServeFormView/SelfServeSignUpForm/api';

const isSelfServeStep = (stepParam: string): boolean =>
  Object.values(SelfServeStepsIdentifiers).includes(stepParam as SelfServeStepsType);

const validateStepParam = (param: string | null) => {
  if (!param) {
    return SelfServeStepsIdentifiers.BusinessDetails;
  }

  const isValidParam = isSelfServeStep(param);

  return isValidParam ? param.toLowerCase() : SelfServeStepsIdentifiers.BusinessDetails;
};

const SelfServeSignupWizard = () => {
  const { search } = useLocation();
  const history = useHistory();

  const [stepParam, setStepParam] = useState<string>(SelfServeStepsIdentifiers.BusinessDetails);
  const queryClient = useQueryClient();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const merchantId = searchParams.get('merchantId') || '';

  const { data: inboundMerchant, isFetching: isFetchingInboundMerchant } = useQuery(
    ['/merchant-admin/inbound-merchants', merchantId],
    () => getInboundMerchant(merchantId),
    {
      refetchOnWindowFocus: false,
    },
  );

  const { data: countryConfigs, isLoading: isCountryConfigsLoading } = useQuery(
    ['merchant-admin/config'],
    getCountryConfigs,
    {
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: isSsmoCountryConfigsLoading, data: ssmoCountryConfig } = useQuery(
    ['/merchant-admin/config/ssmo'],
    getSsmoCountryConfigs,
    {
      refetchOnWindowFocus: false,
    },
  );

  const wizardData: SelfServeWizardStep[] = useMemo(() => {
    if (!inboundMerchant || !ssmoCountryConfig || !countryConfigs) {
      return [];
    }

    return [
      {
        title: 'Service plan',
        identifier: SelfServeStepsIdentifiers.DeviceSelection,
        component: props => (
          <MerchantDeviceSelectionForm
            {...props}
            inboundMerchant={inboundMerchant}
            ssmoCountryConfig={ssmoCountryConfig}
          />
        ),
        percentageFilled: 1 / 12,
      },
      {
        title: 'Service plan',
        identifier: SelfServeStepsIdentifiers.MenuPhotoShoot,
        component: props => (
          <MenuPhotoshopOptionStep {...props} inboundMerchant={inboundMerchant} ssmoCountryConfig={ssmoCountryConfig} />
        ),
        percentageFilled: 2 / 12,
      },
      {
        title: 'Service plan',
        identifier: SelfServeStepsIdentifiers.ServicePricing,
        component: props => (
          <CommissionsStep {...props} inboundMerchant={inboundMerchant} ssmoCountryConfig={ssmoCountryConfig} />
        ),
        percentageFilled: 1 / 4,
      },
      {
        title: 'Complete your business information',
        identifier: SelfServeStepsIdentifiers.BusinessDetails,
        component: props => (
          <BusinessInformationForm {...props} inboundMerchant={inboundMerchant} countryConfigs={countryConfigs} />
        ),
        percentageFilled: 3 / 8,
      },
      {
        title: 'Complete your business information',
        identifier: SelfServeStepsIdentifiers.OpeningHours,
        component: props => <VenueOpeningHoursForm {...props} inboundMerchant={inboundMerchant} />,
        percentageFilled: 1 / 2,
      },
      {
        title: 'Let us know what you sell',
        identifier: SelfServeStepsIdentifiers.ProductList,
        component: props => <ProductListForm {...props} inboundMerchant={inboundMerchant} />,
        percentageFilled: 3 / 4,
      },
      {
        title: 'One last review',
        identifier: SelfServeStepsIdentifiers.Review,
        component: props => <ReviewInfo {...props} country={inboundMerchant.country} />,
        percentageFilled: 1,
      },
    ];
  }, [inboundMerchant, ssmoCountryConfig, countryConfigs]);

  useEffect(() => {
    const stepParam = searchParams.get('step');

    if (!stepParam && wizardData.length > 0) {
      history.push(`${search}&step=${wizardData[0].identifier}`);
      return;
    }

    const newStepParam = validateStepParam(searchParams.get('step'));
    setStepParam(newStepParam);
  }, [history, search, searchParams, wizardData]);

  if (
    isFetchingInboundMerchant ||
    !inboundMerchant ||
    isCountryConfigsLoading ||
    !countryConfigs ||
    isSsmoCountryConfigsLoading ||
    !ssmoCountryConfig ||
    wizardData.length === 0
  ) {
    return (
      <SsmoPageWrapper>
        <Spinner size="large" />
      </SsmoPageWrapper>
    );
  }
  const invalidateCache = () => queryClient.invalidateQueries(['/merchant-admin/inbound-merchants', merchantId]);

  return (
    <SsmoPageWrapper>
      <Wizard
        stepsCount={4}
        invalidateCache={invalidateCache}
        wizardData={wizardData}
        currentStepIdentifier={stepParam}
      />
    </SsmoPageWrapper>
  );
};

export default SelfServeSignupWizard;
